'use client';

/**
 * Page targets for workspace artifact opens ("open the spec at page 3").
 *
 * Artifact opens travel through several panel hosts (org chat, Tech Studio,
 * in-place pages) that only carry a path. Rather than thread a page through
 * each host's panel state, an open records the page here and the PDF viewer
 * claims it: on mount for a newly opened file, or through the subscription
 * when the file is already showing. The open itself still goes through the
 * normal artifact path, so the viewer loads the file with its usual read
 * checks; this store holds only a path and a page number.
 */

type PageRequestListener = (path: string, page: number) => void;

const pendingPages = new Map<string, number>();
const listeners = new Set<PageRequestListener>();

function requestKey(path: string): string {
  return path.trim();
}

export function normalizeWorkspaceArtifactPage(
  page: number | null | undefined
): number | undefined {
  return typeof page === 'number' && Number.isInteger(page) && page >= 1
    ? page
    : undefined;
}

/** Ask the viewer showing `path` to go to `page`; a newer request replaces it. */
export function requestWorkspaceArtifactPage(path: string, page: number): void {
  const key = requestKey(path);
  const target = normalizeWorkspaceArtifactPage(page);
  if (!key || !target) return;
  pendingPages.set(key, target);
  for (const listener of listeners) listener(key, target);
}

/** Claim (and clear) the page requested for `path`, if any. */
export function takeWorkspaceArtifactPageRequest(
  path: string
): number | undefined {
  const key = requestKey(path);
  const page = pendingPages.get(key);
  pendingPages.delete(key);
  return page;
}

/** Hear page requests while a viewer is mounted. Returns the unsubscribe. */
export function subscribeWorkspaceArtifactPageRequests(
  listener: PageRequestListener
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
